import { Check, Copy, Download, Pencil, Trash2 } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import { formatPrefixLabel, PRESET_PREFIXES, type PresetPrefix } from '../../lib/presetPrefixes';

interface SelectionToolbarProps {
  active: PresetPrefix | null;
  count: number;
  counts: Record<PresetPrefix, number>;
  disabled: boolean;
  onBulkDelete: () => void;
  onBulkDownload: () => void;
  onBulkDuplicate: () => void;
  onBulkRename: () => void;
  onClear: () => void;
  onFilterChange: (prefix: PresetPrefix | null) => void;
  onSelectAll: () => void;
  total: number;
}

const SelectionToolbar = ({
  active,
  count,
  counts,
  disabled,
  onBulkDelete,
  onBulkDownload,
  onBulkDuplicate,
  onBulkRename,
  onClear,
  onFilterChange,
  onSelectAll,
  total,
}: SelectionToolbarProps) => (
  <div className="flex flex-col gap-3 border-b border-border-subtle bg-brand-light px-5 py-3.5">
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-vivid">
          <Check className="size-4 text-text-inverse" />
        </span>
        <div className="flex flex-col">
          <span className="text-sm font-bold text-brand-navy">PERSONALIZACIÓN EN LOTE</span>
          <span className="text-xs text-text-muted">
            {`${count} de ${total} seleccionado${count !== 1 ? 's' : ''} · Aplica acciones a los archivos seleccionados.`}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          className="text-xs font-semibold text-brand-vivid hover:underline disabled:opacity-50"
          disabled={disabled}
          onClick={onSelectAll}
          type="button"
        >
          Marcar todo
        </button>
        <span className="text-text-subtle">·</span>
        <button
          className="text-xs font-semibold text-text-muted hover:underline disabled:opacity-50"
          disabled={disabled}
          onClick={onClear}
          type="button"
        >
          Limpiar
        </button>
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-2">
      <button
        className={cn(
          'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
          active === null
            ? 'border-brand-vivid bg-brand-light text-brand-vivid'
            : 'border-border-subtle bg-bg-surface text-text-muted hover:bg-action-ghost-hover'
        )}
        onClick={() => onFilterChange(null)}
        type="button"
      >
        Todos
      </button>
      {PRESET_PREFIXES.map((prefix) => (
        <button
          key={prefix}
          className={cn(
            'rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors',
            active === prefix
              ? 'border-brand-vivid bg-brand-light text-brand-vivid'
              : 'border-border-subtle bg-bg-surface text-text-muted hover:bg-action-ghost-hover'
          )}
          onClick={() => onFilterChange(prefix === active ? null : prefix)}
          type="button"
        >
          {formatPrefixLabel(prefix)}
          <span className="ml-1.5 opacity-60">{counts[prefix]}</span>
        </button>
      ))}
    </div>

    <div className="flex justify-end gap-2">
      <Button
        className="bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
        disabled={disabled || count === 0}
        onClick={onBulkRename}
        size="sm"
        type="button"
      >
        <Pencil className="size-3.5" />
        Editar prefijo
      </Button>

      <Button
        className="bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
        disabled={disabled || count === 0}
        onClick={onBulkDownload}
        size="sm"
        type="button"
      >
        <Download className="size-3.5" />
        Descargar
      </Button>
      <Button
        className="bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
        disabled={disabled || count === 0}
        onClick={onBulkDuplicate}
        size="sm"
        type="button"
      >
        <Copy className="size-3.5" />
        Duplicar
      </Button>
      <Button disabled={disabled || count === 0} onClick={onBulkDelete} size="sm" type="button" variant="destructive">
        <Trash2 className="size-3.5" />
        Eliminar
      </Button>
    </div>
  </div>
);

export default SelectionToolbar;
